import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import useWindowSize from "@/hooks/useWindowSize";
import Link from "next/link";
import { ArrowNarrowRightIcon } from "@heroicons/react/outline";
import * as Text from "@/components/text";
import SvgLogo from "@/components/lib/SvgLogo";
import breakpoints from "@/constants/breakpoinst";
import classNames from "classnames";
import Button from "../actions/Button";
import { motion } from "framer-motion";
import { useGetFilmsByInitiatives } from "@/actions/useMovies";
import Image from "next/legacy/image";
import { useRouter } from 'next/router';


const getInitiativeData = (locale, initiative, field) => {
  const baseLocale = locale?.split('-')[0] || 'es-MX';
  
  return initiativeTranslations[locale]?.[initiative]?.[field] 
    || initiativeTranslations[baseLocale]?.[initiative]?.[field]
    || initiativeTranslations.en[initiative][field];
    
};

const getCommonLabel = (locale, field) => {
  const baseLocale = locale?.split('-')[0] || 'es-MX';
  return initiativeTranslations[locale]?.common?.[field]
    || initiativeTranslations[baseLocale]?.common?.[field]
    || initiativeTranslations.en.common[field];
};

const initiativeTranslations = {
  "es-MX": {
    "gira-ambulante": {
      title: "Gira",
      secondTitle: "Gira de documentales",
      description: "Conoce más de nuestra Gira de cine itinerante.",
    },
    "ambulante-presenta": {
      title: "Presenta",
      secondTitle: "Presenta",
      description: "Circuito de exhibición de cine documental desde el quehacer colaborativo.",
    },
    "ambulante-mas-alla": {
      title: "Más Allá",
      secondTitle: "Más Allá",
      description: "Proyecto de capacitación en producción documental.",
    },
    "ediciones": {
      title: "Ediciones",
      description: "Obras fundamentales para expandir la cultura del cine.",
    },
    "vivero": {
      title: "Vivero",
      description: "Formación en prácticas documentales audiovisuales",
    },
    // "acervo": {
    //   title: "Acervo",
    //   description: "Preservación con perspectiva regional.",
    // },
    "incidencias": {
      title: "Incidencias",
      description: "Campañas sobre causas que merecen ser acompañadas.",
    },
    "distribucion": {
      title: "Distribución",
      description: "Obras fundamentales para expandir la cultura del cine.",
    },
    "becasyfondos": {
      title: "Becas y Fondos",
      description: "Obras fundamentales para expandir la cultura del cine.",
    },
    "proyectosEspeciales": {
      title: "Proyectos Especiales",
      description: "Obras fundamentales para expandir la cultura del cine.",
    },
      common:{
      explore:"Explora",
    }
  },
  "en": {
    "gira-ambulante": {
      title: "Film Festival",
      secondTitle: "Documentary Tour",
      description: "Find out more about our itinerant Film Festival.",
    },
    "ambulante-presenta": {
      title: "Presenta",
      secondTitle: "Presents",
      description: "Documentary exhibition circuit based on collaborative work.",
    },
    "ambulante-mas-alla": {
      title: "Más Allá",
      secondTitle: "Más Allá",
      description: "Documentary production training project.",
    },
    "ediciones": {
      title: "Publishing",
      description: "Fundamental titles to expand film culture.",
    },
    "vivero": {
      title: "Vivero",
      description: "Training in audiovisual documentary practices.",
    },
    // "acervo": {
    //   title: "Archive",
    //   description: "Preservation with a regional perspective.",
    // },
    "incidencias": {
      title: "Advocacy",
      description: "Campaigns for causes that deserve support.",
    },
    "distribucion": {
      title: "Distribution",
      description: "Fundamental works to expand film culture.",
    },
    "becasyfondos": {
      title: "Grants and Funds",
      description: "Fundamental works to expand film culture.",
    },
    "proyectosEspeciales": {
      title: "Special Projects",
      description: "Fundamental works to expand film culture.",
    },
    common: {
      explore: "Explore",
    },
  }
};
const initiatives = (locale) => ({
  "gira-ambulante": {
    accent: "text-gira group-hover:text-neutral-900",
    "positive-text": "text-neutral-300",
    "negative-text": "hover:text-neutral-900 hover:bg-gira",
    "link-positive": "dark",
    "link-negative": "light",
    hr: "border-gira group-hover:border-neutral-900",
    title: getInitiativeData(locale, 'gira-ambulante', 'title'),
    secondTitle: getInitiativeData(locale, 'gira-ambulante', 'secondTitle'),
    description: getInitiativeData(locale, 'gira-ambulante', 'description'),
    link: "/iniciativas/gira/",
  },
  "ambulante-presenta": {
    accent: "text-presenta group-hover:text-neutral-900",
    "positive-text": "text-neutral-300",
    "negative-text": "hover:text-neutral-900 hover:bg-presenta",
    "link-positive": "dark",
    "link-negative": "light",
    hr: "border-presenta group-hover:border-neutral-900",
    title: getInitiativeData(locale,'ambulante-presenta','title'),
    secondTitle: getInitiativeData(locale,'ambulante-presenta','secondTitle'),
    description: getInitiativeData(locale,'ambulante-presenta','description'),
    link: "/ambulante-presenta",
  },
  "ambulante-mas-alla": {
    accent: "text-mas-alla group-hover:text-neutral-900",
    "positive-text": "text-neutral-300",
    "negative-text": "hover:text-neutral-900 hover:bg-mas-alla",
    "link-positive": "dark",
    "link-negative": "light",
    hr: "border-mas-alla group-hover:border-neutral-900",
    title: getInitiativeData(locale,'ambulante-mas-alla','title'),
    secondTitle: getInitiativeData(locale,'ambulante-mas-alla','secondTitle'),
    description: getInitiativeData(locale,'ambulante-mas-alla','description'),
    link: "/ambulante-mas-alla",
  },
  ediciones: {
    accent: "text-ediciones group-hover:text-neutral-900",
    "positive-text": "text-neutral-300",
    "negative-text": "hover:text-neutral-900 hover:bg-ediciones",
    "link-positive": "dark",
    "link-negative": "light",
    hr: "border-ediciones group-hover:border-neutral-900",
    title:getInitiativeData(locale,'ediciones','title'),
    description:getInitiativeData(locale,'ediciones','description'),
    link: "/ambulante-ediciones",
  },
  // acervo: {
  //   accent: "text-acervo group-hover:text-neutral-900",
  //   "positive-text": "text-neutral-300",
  //   "negative-text": "hover:text-neutral-900 hover:bg-acervo",
  //   "link-positive": "dark",
  //   "link-negative": "light",
  //   hr: "border-acervo group-hover:border-neutral-900",
  //   title: "Acervo",
  //   description:getInitiativeData(locale,'acervo','description'),
  //   link: "",
  // },
  incidencias: {
    accent: "text-incidencias group-hover:text-neutral-900",
    "positive-text": "text-neutral-300",
    "negative-text": "hover:text-neutral-900 hover:bg-incidencias",
    "link-positive": "dark",
    "link-negative": "light",
    hr: "border-incidencias group-hover:border-neutral-900",
    title: getInitiativeData(locale,'incidencias','title'),
    description: getInitiativeData(locale,'incidencias','description'),
    link: "/incidencias",
  },
  distribucion: {
    accent: "text-distribucion group-hover:text-neutral-900",
    "positive-text": "text-neutral-300",
    "negative-text": "hover:text-neutral-900 hover:bg-distribucion",
    "link-positive": "dark",
    "link-negative": "light",
    hr: "border-distribucion group-hover:border-neutral-900",
    title: getInitiativeData(locale,'distribucion','title'),
    description: getInitiativeData(locale,'distribucion','description'),
    link: "/distribucion",
  },
  becasyfondos: {
    accent: "text-becasyfondos group-hover:text-neutral-900",
    "positive-text": "text-neutral-300",
    "negative-text": "hover:text-neutral-900 hover:bg-becasyfondos",
    "link-positive": "dark",
    "link-negative": "light",
    hr: "border-becasyfondos group-hover:border-neutral-900",
    title: getInitiativeData(locale,'becasyfondos','title'),
    description: getInitiativeData(locale,'becasyfondos','description'),
    link: "/becas-y-fondos",
  },
  proyectosEspeciales: {
    accent: "text-proyectos-especiales group-hover:text-neutral-900",
    "positive-text": "text-neutral-300",
    "negative-text": "hover:text-neutral-900 hover:bg-proyectos-especiales",
    "link-positive": "dark",
    "link-negative": "light",
    hr: "border-proyectos-especiales group-hover:border-neutral-900",
    title: getInitiativeData(locale,'proyectosEspeciales','title'),
    description:getInitiativeData(locale,'proyectosEspeciales','description'),
    link: "/proyectos-especiales",
  },
  vivero: {
    accent: "text-vivero group-hover:text-neutral-900",
    "positive-text": "text-neutral-300",
    "negative-text": "hover:text-neutral-900 hover:bg-vivero",
    "link-positive": "dark",
    "link-negative": "light",
    hr: "border-vivero group-hover:border-neutral-900",
    title:getInitiativeData(locale,'vivero','title'),
    description:getInitiativeData(locale,'vivero','description'),
    link: "/vivero",
  },
});

export default function InitiativeCard({ h = "",  inititative,currentLocale, ...rest }) {
  const windowSize = useWindowSize();
  const cssObject = initiatives(currentLocale)[inititative];
  const css = classNames(
    cssObject["positive-text"],
    cssObject["negative-text"],
    `bg-black transition-all duration-150 w-full p-5 md:p-10 h-full group relative`
  );
  const [linkState, setLinkState] = useState(cssObject["link-positive"]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7 }}
      whileInView={{ opacity: 1, scale: 1 }}
      {...rest}
      className={css}
      onMouseEnter={() => setLinkState(cssObject["link-negative"])}
      onMouseLeave={() => setLinkState(cssObject["link-positive"])}
    >
      <Link
        href={cssObject.link}
        passHref
        className="text-white group-hover:text-neutral-900 flex flex-col h-full"
      >
        <>
          <div className="w-2/3">
            <SvgLogo className="text-white group-hover:text-neutral-900"></SvgLogo>
          </div>

          <Text.Label
            as="p"
            fontWeight="bold"
            size={windowSize.width < breakpoints.lg ? "md" : "xl"}
            className={classNames(cssObject.accent, "mb-2 mt-2 md:mb-5")}
          >
            {cssObject.title}
          </Text.Label>
          <hr className={classNames(cssObject.hr, "bt-2 pb-5")}></hr>
          <Text.Body
            size={windowSize.width < 720 ? "md" : "xl"}
            className="mt-2 md:mt-5 flex-grow"
          >
            {cssObject.description}
          </Text.Body>

          {windowSize.width < breakpoints.md ? (
            <div className="relative bottom-0 mt-3 items-baseline ">
              <Button
                size="lg"
                color="terciary-dark"
                label={getCommonLabel(currentLocale, 'explore')}
                className="w-3/4"
              ></Button>
            </div>
          ) : (
            <div className="bottom-0 mt-3 items-baseline pb-4 xl:relative ">
              <Text.Link
                size={windowSize.width < breakpoints.lg ? "lg" : "xl"}
                theme={linkState}
                label={getCommonLabel(currentLocale, 'explore')}
                trailingIcon={<ArrowNarrowRightIcon />}
              ></Text.Link>
              &nbsp;
            </div>
          )}
        </>
      </Link>
    </motion.div>
  );
}

InitiativeCard.propTypes = {
  locale: PropTypes.string,
  inititative: PropTypes.oneOf([
    "ambulante-presenta",
    "ambulante-mas-alla",
    "ediciones",
    "gira-ambulante",
    "vivero",
  ]),
};

export function InitiativeCardSimple({ h = "", inititative,currentLocale, ...rest }) {
  const windowSize = useWindowSize();
  const cssObject = initiatives(currentLocale)[inititative];
  const css = classNames(
    cssObject["positive-text"],
    cssObject["negative-text"],
    `hover:cursor-pointer bg-black transition-all duration-150 md:aspect-square w-full sm:p-6 md:p-6 lg:p-8 h-[${h}] group relative`
  );
  const [linkState, setLinkState] = useState(cssObject["link-positive"]);
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      {...rest}
      className={css}
      onMouseEnter={() => setLinkState(cssObject["link-negative"])}
      onMouseLeave={() => setLinkState(cssObject["link-positive"])}
    >
      <div className="w-36">
        <Image
          src="/next_assets/img/brand-assets/AmbulanteLogo.png"
          width={100}
          height={15}
          alt="logo"
          layout="responsive"
        />

        {/* <SvgLogo className="text-white group-hover:text-neutral-900"></SvgLogo> */}
      </div>

      <Text.Label
        as="p"
        fontWeight="bold"
        size={windowSize.width < breakpoints.lg ? "md" : "xl"}
        className={classNames(
          cssObject.accent,
          "my-2 sm:text-lg md:mb-2 md:text-sm"
        )}
      >
        {cssObject.secondTitle}
      </Text.Label>
      <Text.Body
        as="p"
        size={windowSize.width < 720 ? "md" : "xl"}
        className="mt-2 leading-5 md:mt-2 md:text-[14px]"
      >
        {cssObject.description}
      </Text.Body>
    </motion.div>
  );
}

InitiativeCardSimple.propTypes = {
  locale: PropTypes.string,
  inititative: PropTypes.oneOf([
    "ambulante-presenta",
    "ambulante-mas-alla",
    "ediciones",
    "gira-ambulante",
    "ambulante-presenta",
    "vivero",
  ]),
};